import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import TriggerHelp from "./TriggerHelp";
import DownloadPDF from "./DownloadPDF";
import {
  formatTimeStamp,
  LONG_DISPLAY_FORMAT,
} from "../utils/collectionFound/dateTimeUtils";
import { DateTime } from "luxon";
import { formatHexStr } from "../utils/formatHexStr";
import { HASH_ALGORITHM_SHA3_256 } from "../utils/cryptoUtils";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import {
  Accordion,
  ActionIcon,
  Box,
  CopyButton,
  Flex,
  Image,
  Text,
  Tooltip,
  useMantineTheme,
  Title,
  TextInput,
  Loader,
} from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { useStylesStampResult } from "../utils/mantine/useStylesStampResult";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import StampDetail from "./StampDetail";
import { useStylesAccordion } from "../utils/mantine/useStylesAccordion";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";
import { createVerifiedFileDownload, URLs } from "../utils/apis";
import { useStylesFileManager } from "../utils/mantine/useStylesFileManager";
import { formatFileSize } from "../utils/formatFileSize";

const StampCreated = ({
  data,
  staticBasePath,
  handleDefaultScreen,
  allHashes,
  selectedFile,
  text,
  fileName,
  normalizedPortfolio,
  verificationMethod,
}) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  //css Classes
  const { classes: classesStampResult } = useStylesStampResult();
  const { classes: classesPage } = useStylesPage();
  const { classes: tooltipClasses } = useStylesTooltip();
  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();
  const { classes: classesAccordion } = useStylesAccordion();
  const { classes: classesIcons } = useStylesIcons();

  const { classes: classesFileManager } = useStylesFileManager();

  // in development
  const [pdfShow] = useState(true);

  // current hash
  const [currentHashVersion, setCurrentHashVersion] = useState("");

  useEffect(() => {
    setCurrentHashVersion("");

    if (allHashes[data.objectCid]) {
      //check exists current hash in all hashes
      setCurrentHashVersion(allHashes[data.objectCid]);
    }
  }, [data.objectCid, allHashes]);

  const formattedDateTime = formatTimeStamp(
    data.timestamp,
    data.display_timezone,
    LONG_DISPLAY_FORMAT,
  );

  const timeZone = data.display_timezone ? data.display_timezone : "UTC";

  const [helpIsActive, setHelpIsActive] = useState(() => {
    const cookieValue = Cookies.get("helpIsActive");
    return cookieValue !== undefined ? JSON.parse(cookieValue) : true;
  });

  const handleHelpTriggerClick = () => {
    const newHelpIsActive = !helpIsActive;
    setHelpIsActive(newHelpIsActive);
    Cookies.set("helpIsActive", JSON.stringify(newHelpIsActive));
  };

  const [isNearBottom, setIsNearBottom] = useState(false);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const remainingScroll = documentHeight - (scrollTop + windowHeight);

    if (
      (remainingScroll <= 80 && window.innerWidth <= 639) ||
      (remainingScroll <= 48 && window.innerWidth >= 640)
    ) {
      setIsNearBottom(true);
    } else {
      setIsNearBottom(false);
    }
  };

  const [loadingCreateLink, setLoadingCreateLink] = useState(false);
  const [stampCreatedClass, setStampCreatedClass] = useState("");
  const [linkID, setLinkID] = useState("");
  useEffect(() => {
    if (selectedFile && text) {
      setStampCreatedClass("stamp-created-text");

      const fileData = {
        name: fileName,
        collectionName: data.collection_name,
        metadata: {
          object_cid: data.objectCid,
          timestamp: data.timestamp,
          user: data.user_id,
        },
      };

      handleCreateLinkFile(fileData);
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedFile, text]);

  const handleCreateLinkFile = async (fileData) => {
    setLoadingCreateLink(true);
    const ResponseData = await createVerifiedFileDownload(
      fileData,
      setLoadingCreateLink,
    );
    ResponseData?.data?.data?.link_id &&
      setLinkID(ResponseData?.data?.data?.link_id);
  };

  const handleDownloadFile = () => {
    if (selectedFile && text) {
      const url = URL.createObjectURL(selectedFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = selectedFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadNormalizedCSV = () => {
    if (normalizedPortfolio) {
      // Create a Blob from the normalized CSV content
      const blob = new Blob([normalizedPortfolio], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Generate filename: vBase-portfolio_collectionName_timestamp.csv (for portfolio stamps)
      const collectionName = data.collection_name || "portfolio";
      // Sanitize collection name: remove special characters, replace spaces with underscores
      const sanitizedCollectionName = collectionName
        .replace(/[^a-zA-Z0-9_-]/g, "_")
        .replace(/\s+/g, "_")
        .toLowerCase();

      // Format timestamp for filename (YYYY-MM-DD_HH-MM-SS+TZ)
      // Use UTC timestamp to match Stamped File Storage format
      // Use the same parsing logic as formatTimeStamp to handle backend timestamp format
      let timestampStr = "";
      try {
        if (data.timestamp) {
          // Try parsing with supported formats (same as dateTimeUtils)
          const formats = ["yyyy-MM-dd HH:mm:ssZZ", "yyyy-MM-dd'T'HH:mm:ssZZ"];
          let dateTime = DateTime.invalid("Invalid date format");

          for (const format of formats) {
            dateTime = DateTime.fromFormat(data.timestamp, format, {
              zone: "UTC",
            });
            if (dateTime.isValid) {
              break;
            }
          }

          // Fallback to ISO parsing if format parsing failed
          if (!dateTime.isValid) {
            dateTime = DateTime.fromISO(data.timestamp, { zone: "UTC" });
          }

          // Format in UTC to match Stamped File Storage
          if (dateTime.isValid) {
            timestampStr = dateTime
              .toUTC()
              .toFormat("yyyy-MM-dd_HH-mm-ssZZ")
              .replace(/:/g, ""); // Remove all colons from timezone offset (+00:00 -> +0000)
          } else {
            // Fallback to current timestamp in UTC
            timestampStr = DateTime.utc()
              .toFormat("yyyy-MM-dd_HH-mm-ssZZ")
              .replace(/:/g, "");
          }
        } else {
          // Fallback to current timestamp in UTC
          timestampStr = DateTime.utc()
            .toFormat("yyyy-MM-dd_HH-mm-ssZZ")
            .replace(/:/g, "");
        }
      } catch (e) {
        // Fallback to current timestamp in UTC
        timestampStr = DateTime.utc()
          .toFormat("yyyy-MM-dd_HH-mm-ssZZ")
          .replace(/:/g, "");
      }

      // Use vBase-portfolio_ prefix for portfolio stamps, vBase_ for others
      const filenamePrefix =
        verificationMethod === "portfolio"
          ? `vBase-portfolio_${sanitizedCollectionName}`
          : `vBase_${sanitizedCollectionName}`;
      a.download = `${filenamePrefix}_${timestampStr}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Box
      className={`${classesStampResult.stamp} ${classesPage.page} stamp-result ${stampCreatedClass}`}
    >
      {!text && (
        <>
          <Image
            src={`${staticBasePath}public/images/svg/vb-icon-verified.svg`}
            alt="Stamp Created!"
            className={classesPage.pageHeaderIcon}
          />

          <Title order={1}>Stamp Created!</Title>
        </>
      )}
      {selectedFile && text && (
        <CustomAnchor
          href="#"
          text={`Download Stamped Content (${formatFileSize(
            selectedFile.size,
          )}, HTML)`}
          IconDownloadFile="true"
          componentClasses={`link file-found-header-link ${
            !linkID && "file-found-header-link-without-link-id"
          }`}
          onClick={(e) => {
            e.preventDefault();
            handleDownloadFile();
          }}
        />
      )}

      {verificationMethod === "portfolio" && normalizedPortfolio && (
        <CustomAnchor
          href="#"
          text="Download Portfolio CSV"
          IconDownloadFile="true"
          componentClasses={`link file-found-header-link ${
            !linkID && "file-found-header-link-without-link-id"
          }`}
          onClick={(e) => {
            e.preventDefault();
            handleDownloadNormalizedCSV();
          }}
        />
      )}

      {loadingCreateLink && (
        <Loader
          className={`${classesFileManager.linkLoader} verified-downloaded-link-loader`}
        />
      )}

      {selectedFile && text && linkID && (
        <>
          <Box className="verified-downloaded-link">
            <Text>Shareable Authenticity Link</Text>
            <Flex className={classesFileManager.alertBlock}>
              {" "}
              <TextInput
                classNames={{
                  root: classesFileManager.alertInput,
                }}
                disabled={true}
                value={`${window.location.origin}${URLs.VERIFY_URL}?link-id=${linkID}`}
              />
              <CopyButton
                value={`${window.location.origin}${URLs.VERIFY_URL}?link-id=${linkID}`}
                timeout={2000}
              >
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? "Copied" : "Copy"}
                    className={classesIcons.copy}
                    withArrow
                    position="right"
                  >
                    <ActionIcon
                      color={
                        copied
                          ? theme.customColors.actionIconFirst
                          : theme.customColors.actionIconSecond
                      }
                      variant="subtle"
                      onClick={copy}
                    >
                      {copied ? (
                        <IconCheck className={classesIcons.width} />
                      ) : (
                        <IconCopy className={classesIcons.width} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Flex>
          </Box>
          <Text component="h2">Authenticity Stamp</Text>
        </>
      )}

      <StampDetail
        data={data}
        staticBasePath={staticBasePath}
        helpIsActive={helpIsActive}
        timeZone={timeZone}
        id="stamp-created"
      ></StampDetail>

      <Box
        className={`${classesTechnicalDetail.technicalBlock} ${classesTechnicalDetail.technical}`}
      >
        <Accordion
          className={classesAccordion.accordion}
          value="tech"
          variant="contained"
          chevron={null}
        >
          <Accordion.Item value="tech">
            <Accordion.Control component="div">
              <Flex gap={theme.flex.gap.xs}>
                <Text className="title">Stamp Details</Text>

                {helpIsActive && (
                  <CustomTooltip
                    content={
                      <>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Blockchain Address
                          </Text>{" "}
                          is the blockchain address of the user who made the
                          stamp.
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Content ID
                          </Text>{" "}
                          indicates whether vBase has confirmed the identity of
                          the user.
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Collection ID
                          </Text>{" "}
                          is a fingerprint of the Collection Name.
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Public Blockchain Record
                          </Text>{" "}
                          is a link to a third-party tool showing the permanent
                          location of your data on a blockchain!
                        </Text>
                      </>
                    }
                  ></CustomTooltip>
                )}
              </Flex>
            </Accordion.Control>
            <Accordion.Panel>
              <Box>
                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">Blockchain Address</Text>
                  <Box className="dots"></Box>
                  <Text className="value" id="stamp-found__account_id">
                    <CustomAnchor
                      text={formatHexStr(data.user_id)}
                      componentClasses="link"
                      color={theme.customColors.black}
                      href={`/verify/user-data/?user=${data.user_id}`}
                    />
                    <CopyButton value={data.user_id} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">
                    Content ID{" "}
                    {currentHashVersion && <>({currentHashVersion})</>}
                  </Text>
                  <Box className="dots"></Box>
                  <Text className="value" id="stamp-found__content-id">
                    {formatHexStr(data.objectCid)}

                    <CopyButton value={data.objectCid} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">
                    {" "}
                    Collection ID ({HASH_ALGORITHM_SHA3_256})
                  </Text>
                  <Box className="dots"></Box>
                  <Text className="value">
                    {data.collection_hash ? (
                      <CustomAnchor
                        text={
                          data.collection_hash
                            ? formatHexStr(data.collection_hash)
                            : "<<None>>"
                        }
                        componentClasses="link collection-hash"
                        color={theme.customColors.black}
                        href={`/verify/user-data/?user=${
                          data.user || data.user_id
                        }&collection_cid=${data.collection_hash}`}
                      />
                    ) : (
                      "<<None>>"
                    )}
                    <CopyButton
                      value={data.collection_hash ?? ""}
                      timeout={2000}
                    >
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            style={{
                              opacity: data.collection_hash ? 1 : 0,
                              pointerEvents: data.collection_hash
                                ? "auto"
                                : "none",
                            }}
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">Public Blockchain Record</Text>
                  <Box className="dots"></Box>
                  <Text className="value">
                    <CustomAnchor
                      target="_blank"
                      text={`${data.blockExplorerUrl}${formatHexStr(
                        data.transactionHash,
                        10,
                        0,
                        20,
                        true,
                      )}...`}
                      componentClasses="link"
                      color={theme.customColors.black}
                      href={`${data.blockExplorerUrl}${data.transactionHash}`}
                    />

                    <CopyButton
                      value={`${data.blockExplorerUrl}${data.transactionHash}`}
                      timeout={2000}
                    >
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                {pdfShow && (
                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item download-pdf-item"
                    align={theme.flex.align.end}
                  >
                    <DownloadPDF
                      data={data}
                      formattedDateTime={formattedDateTime + " " + timeZone}
                      allHashes={allHashes}
                    />
                  </Flex>
                )}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>

      <Flex
        align={theme.flex.align.center}
        justify={theme.flex.justify.center}
        mt={theme.margin.xxlEm}
        gap={theme.flex.gap.xs}
      >
        <CustomButton
          text="Make Another Stamp"
          onClick={handleDefaultScreen}
          componentClasses="buttonBlack buttonBlack-verify anchor"
        ></CustomButton>
      </Flex>
      <TriggerHelp
        helpIsActive={helpIsActive}
        isNearBottom={isNearBottom}
        isAuthenticated="true"
        isOpenNotification={false}
        handleHelpTriggerClick={handleHelpTriggerClick}
      />
    </Box>
  );
};

StampCreated.propTypes = {
  data: PropTypes.shape({
    objectCid: PropTypes.string,
    time: PropTypes.string,
    user: PropTypes.string,
    user_id: PropTypes.string,
    authenticated: PropTypes.bool,
    collection_name: PropTypes.string,
    timedelta: PropTypes.string,
    collection_hash: PropTypes.string,
    blockExplorerUrl: PropTypes.string,
    transactionHash: PropTypes.string,
    timestamp: PropTypes.string,
    display_timezone: PropTypes.string,
  }).isRequired,
  text: PropTypes.string,
  fileName: PropTypes.string,
  staticBasePath: PropTypes.string.isRequired,
  handleDefaultScreen: PropTypes.func.isRequired,
  showVerifyStamp: PropTypes.bool,
  normalizedPortfolio: PropTypes.string,
  verificationMethod: PropTypes.string,
  allHashes: PropTypes.object.isRequired,
  selectedFile: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
  }),
};

export default StampCreated;
